import "./WalletTopUpModal.css";

import React, { useMemo, useState } from "react";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import {
  confirmStripeWalletTopup,
  createStripeWalletTopupIntent,
} from "Config/API/action";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

const stripePromise = loadStripe(
  "pk_live_51N8M1pJ0g5mwZtdKZiqmqBwCqRTUQbv0zyd7GM85qOUMmdcMMVSunMl31kdmdXLcJIkCNOoa08HPimAdgeqW1YPD00qJlIPjcT"
);

const WalletTopUpPaymentForm = ({
  attemptState,
  setAttemptState,
  token,
  onSuccess,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const handleConfirmPayment = async () => {
    if (!stripe || !elements || attemptState.confirming) return;
    setAttemptState((prev) => ({ ...prev, confirming: true }));

    const result = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
      confirmParams: {
        return_url: `${window.location.origin}/money-credit-dashboard`,
      },
    });

    if (result?.error) {
      if (result.error.type === "card_error" || result.error.type === "validation_error") {
        toast(result.error.message || "Payment failed. Please verify card details.", {
          type: "error",
        });
      } else {
        toast("Payment requires action. Please complete the authentication step.", {
          type: "info",
        });
      }
      setAttemptState((prev) => ({ ...prev, confirming: false }));
      return;
    }

    const paymentIntent = result?.paymentIntent;
    if (!paymentIntent?.id) {
      toast("Payment verification failed. Please try again.", { type: "error" });
      setAttemptState((prev) => ({ ...prev, confirming: false }));
      return;
    }

    if (paymentIntent.status === "requires_action") {
      toast("Payment requires action. Please complete authentication and retry.", {
        type: "info",
      });
      setAttemptState((prev) => ({ ...prev, confirming: false }));
      return;
    }

    const confirmRes = await confirmStripeWalletTopup(token, paymentIntent.id);
    if (!confirmRes?.status) {
      const message = (confirmRes?.message || "").toLowerCase();
      if (message.includes("not succeeded")) {
        toast("Payment is not succeeded yet. Please wait a moment and retry confirm.", {
          type: "info",
        });
      } else if (message.includes("transfer") || message.includes("credit")) {
        toast("Payment received but transfer/credit failed. Please contact support.", {
          type: "error",
        });
      } else {
        toast("Top-up confirmation failed. Please try again.", { type: "error" });
      }
      setAttemptState((prev) => ({ ...prev, confirming: false }));
      return;
    }

    await onSuccess();
    toast("Wallet credited successfully. You can continue subscription purchase", {
      type: "success",
    });
    setAttemptState((prev) => ({ ...prev, confirming: false }));
  };

  return (
    <>
      <PaymentElement />
      <div className="wallet-topup-actions">
        <button
          className="wallet-topup-primary"
          onClick={handleConfirmPayment}
          disabled={attemptState.confirming || !stripe || !elements}
        >
          {attemptState.confirming ? "Processing..." : "Confirm Top Up"}
        </button>
      </div>
    </>
  );
};

const WalletTopUpModal = ({
  hide,
  token,
  dashboardUrl,
  shortfallAmount = null,
  onSuccess,
}) => {
  const loggedInUserDetails = useSelector(
    (state) => state.authenticationReducer
  );
  const canTopUpWallet = loggedInUserDetails?.add_credit_rights === "manager";
  const canManageStripe =
    loggedInUserDetails?.credit_account_manager === "manager";
  const [amount, setAmount] = useState(shortfallAmount || "");
  const [attemptState, setAttemptState] = useState({
    creatingIntent: false,
    confirming: false,
    clientSecret: "",
    amountForIntent: null,
  });

  const options = useMemo(
    () => ({ clientSecret: attemptState.clientSecret }),
    [attemptState.clientSecret]
  );

  const handleCreateIntent = async () => {
    if (!canTopUpWallet) return;
    if (attemptState.creatingIntent || attemptState.confirming) return;
    const numericAmount = Number(amount);
    if (!numericAmount || numericAmount <= 0) {
      toast("Please enter a valid top-up amount.", { type: "info" });
      return;
    }

    // if (
    //   attemptState.clientSecret &&
    //   Number(attemptState.amountForIntent) === numericAmount
    // ) {
    //   toast("Intent already created for this attempt. Continue payment below.", {
    //     type: "info",
    //   });
    //   return;
    // }

    setAttemptState((prev) => ({ ...prev, creatingIntent: true, clientSecret: "" }));
    // const res = await createStripeWalletTopupIntent(token, numericAmount);
    // if (!res?.status) {
    //   toast("Top-up intent create failed. Please try again.", { type: "error" });
    //   setAttemptState((prev) => ({ ...prev, creatingIntent: false }));
    //   return;
    // }

    // const clientSecret =
    //   res?.data?.client_secret || res?.data?.clientSecret || res?.client_secret;
    // if (!clientSecret) {
    //   toast("Top-up intent create failed. Missing client secret.", { type: "error" });
    //   setAttemptState((prev) => ({ ...prev, creatingIntent: false }));
    //   return;
    // }
try {
      const res = await createStripeWalletTopupIntent(token, numericAmount);
      const checkoutUrl = res?.data?.url;
      if (checkoutUrl) {
        window.open(checkoutUrl, "_blank");
        return;
      }
 
      toast(res?.message || "Unable to create payment link. Please try again.", {
        type: "error",
      });
    } catch (error) {
      toast("Unable to create payment link. Please try again.", { type: "error" });
    } finally {
     
    setAttemptState((prev) => ({
      ...prev,
      creatingIntent: false,
      amountForIntent: numericAmount,
    }));
  }
  };

  return (
    <div className="wallet-topup-overlay">
      <div className="wallet-topup-modal">
        <div className="wallet-topup-header">
          <h3>Top Up Wallet</h3>
          <span className="wallet-topup-close" onClick={() => hide(false)}>
            X
          </span>
        </div>

        {shortfallAmount ? (
          <div className="wallet-topup-note">
            Insufficient balance. Shortfall: {Number(shortfallAmount).toFixed(2)} credits.
          </div>
        ) : null}

        <input
          type="number"
          className="wallet-topup-field"
          placeholder="Enter top-up amount"
          value={amount}
          min="1"
          onChange={(e) => setAmount(e.target.value)}
          disabled={attemptState.creatingIntent || attemptState.confirming}
        />

        {(canTopUpWallet || canManageStripe) && (
          <div className="wallet-topup-actions">
            {canTopUpWallet && (
              <button
                className="wallet-topup-primary"
                onClick={handleCreateIntent}
                disabled={attemptState.creatingIntent || attemptState.confirming}
              >
                {attemptState.creatingIntent
                  ? "Creating Intent..."
                  : "Top Up Wallet"}
              </button>
            )}
            {(canManageStripe && dashboardUrl) && (
              <button
                className="wallet-topup-secondary"
                onClick={() => window.open(dashboardUrl, "_blank")}
              >
                Manage Stripe Account
              </button>
            )}
          </div>
        )}

        {attemptState.clientSecret ? (
          <Elements stripe={stripePromise} options={options}>
            <WalletTopUpPaymentForm
              attemptState={attemptState}
              setAttemptState={setAttemptState}
              token={token}
              onSuccess={onSuccess}
            />
          </Elements>
        ) : null}
      </div>
    </div>
  );
};

export default WalletTopUpModal;
